import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import UserLogoutWidget from "./UserLogoutWidget";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CategoryIcon from "@mui/icons-material/Category";
import SettingsIcon from "@mui/icons-material/Settings";
import PublicIcon from "@mui/icons-material/Public";
import RedditIcon from "@mui/icons-material/Reddit";
import ForumIcon from "@mui/icons-material/Forum";
import { useNavigate, useLocation } from "react-router";
import { useThemeSwitcher } from "../theme/ThemeContext";
import { useModules } from "../ModuleContext";
import ThemePalette from "./ThemePalette";
import getEnv from "../config";
import { getCrawlTasks } from "../services/web-services";
import { getRedditTasks } from "../services/reddit_services";
import { getMastodonTasks } from "../services/mastodon_services";

const REACT_APP_MISP_URL = getEnv("REACT_APP_MISP_URL");
const drawerWidth = 240;
const collapsedWidth = 64;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  minHeight: theme.mixins.toolbar.minHeight,
  position: "relative",
  zIndex: 1,
}));

const MyContent = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "noPadding",
})(({ theme, open, noPadding }) => ({
  flexGrow: 1,
  minWidth: 0,
  padding: noPadding ? 0 : theme.spacing(3),
  ...(noPadding
    ? {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }
    : {}),
}));

const SectionLabel = ({ open, children }) =>
  open ? (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        px: 2,
        pt: 2,
        pb: 0.5,
        color: "text.disabled",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontSize: "0.65rem",
      }}
    >
      {children}
    </Typography>
  ) : (
    <Box sx={{ my: 1, mx: 1.5, borderTop: 1, borderColor: "divider", opacity: 0.5 }} />
  );

const NavItem = ({
  path,
  icon,
  label,
  onClick,
  external,
  active,
  open,
  badge,
  onHover,
  onHoverLeave,
}) => {
  const button = (
    <ListItem disablePadding>
      <ListItemButton
        onClick={onClick || (() => (external ? window.open(path, "_blank") : null))}
        onMouseEnter={onHover}
        onMouseLeave={onHoverLeave}
        sx={{
          position: "relative",
          minHeight: 44,
          justifyContent: open ? "initial" : "center",
          ...(active && {
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "20%",
              bottom: "20%",
              width: 3,
              borderRadius: "0 3px 3px 0",
              bgcolor: "secondary.main",
            },
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 3 : "auto",
            justifyContent: "center",
            color: active ? "secondary.main" : "text.secondary",
            transition: "color 0.2s ease",
            position: "relative",
          }}
        >
          {!open && badge > 0 ? (
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              {icon}
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -8,
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                  borderRadius: "10px",
                  minWidth: 18,
                  height: 16,
                  px: 0.5,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                {badge > 99 ? "99+" : badge}
              </Box>
            </Box>
          ) : (
            icon
          )}
        </ListItemIcon>
        {open && (
          <>
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontWeight: active ? 600 : 400,
                color: active ? "text.primary" : "text.secondary",
                fontSize: "0.875rem",
              }}
            />
            {badge > 0 && (
              <Chip
                size="small"
                label={badge}
                sx={{
                  height: 18,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </>
        )}
      </ListItemButton>
    </ListItem>
  );

  return open ? button : <Tooltip title={label} placement="right">{button}</Tooltip>;
};

const Page = (props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { modules } = useModules();
  const { branding } = useThemeSwitcher();
  const [open, setOpen] = React.useState(true);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [gatheringStats, setGatheringStats] = React.useState({ web: 0, reddit: 0, mastodon: 0 });
  const [gatheringAnchor, setGatheringAnchor] = React.useState(null);
  const hoverTimerRef = React.useRef(null);
  const location = useLocation();
  const clickTimestamps = React.useRef([]);

  const isFullBleed = location.pathname === '/ontologies' || location.pathname === '/taxonomies';

  const navProps = (path, external) => ({
    open,
    active: !external && location.pathname === path,
    onClick: external ? () => window.open(path, "_blank") : () => navigate(path),
  });

  const toggleDrawer = () => setOpen((prev) => !prev);

  const handleLogoClick = () => {
    const now = Date.now();
    clickTimestamps.current = clickTimestamps.current.filter((t) => now - t < 2000);
    clickTimestamps.current.push(now);
    if (clickTimestamps.current.length >= 3) {
      clickTimestamps.current = [];
      setPaletteOpen(true);
    }
  };

  React.useEffect(() => {
    (async () => {
      try {
        const [web, reddit, masto] = await Promise.allSettled([
          modules.crawlers ? getCrawlTasks() : Promise.resolve([]),
          modules.social_crawlers ? getRedditTasks() : Promise.resolve([]),
          modules.social_crawlers ? getMastodonTasks() : Promise.resolve([]),
        ]);
        setGatheringStats({
          web: web.status === "fulfilled" ? (web.value || []).length : 0,
          reddit: reddit.status === "fulfilled" ? (reddit.value || []).length : 0,
          mastodon: masto.status === "fulfilled" ? (masto.value || []).length : 0,
        });
      } catch {
        // silent
      }
    })();
  }, [modules.crawlers, modules.social_crawlers, location.pathname]);

  const gatheringTotal = gatheringStats.web + gatheringStats.reddit + gatheringStats.mastodon;

  const openGatheringPreview = (e) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    const target = e.currentTarget;
    hoverTimerRef.current = setTimeout(() => setGatheringAnchor(target), 350);
  };

  const closeGatheringPreview = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setGatheringAnchor(null);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {!isFullBleed && (
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <Box sx={{ flexGrow: 1 }} />
            <UserLogoutWidget />
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            boxSizing: "border-box",
          },
        }}
      >
        <DrawerHeader>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}${branding.logo}`}
            alt={`${branding.name} Logo`}
            onClick={handleLogoClick}
            sx={{
              maxHeight: 50,
              maxWidth: open ? 160 : 40,
              width: "100%",
              objectFit: "contain",
              transition: "all 0.3s",
              mx: "auto",
              my: 1,
              display: "block",
              cursor: "pointer",
              userSelect: "none",
            }}
          />

          <IconButton onClick={toggleDrawer} sx={{ mt: 1 }}>
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>

        <Divider />

        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <SectionLabel open={open}>Gathering</SectionLabel>
          <List sx={{ px: 0.5, py: 0 }}>
            <NavItem icon={<HomeIcon />} label="Dashboard" {...navProps("/")} />
            {(modules.crawlers || modules.social_crawlers) && (
              <NavItem
                icon={<SearchRoundedIcon />}
                label="Information Gathering"
                badge={gatheringTotal}
                onHover={openGatheringPreview}
                onHoverLeave={closeGatheringPreview}
                {...navProps("/results")}
              />
            )}
          </List>

          <SectionLabel open={open}>Intelligence</SectionLabel>
          <List sx={{ px: 0.5, py: 0 }}>
            {REACT_APP_MISP_URL && (
              <NavItem
                icon={
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/misp-logo.png`}
                    alt="MISP"
                    sx={{ width: 25, height: 25, opacity: 0.85 }}
                  />
                }
                label="Information Sharing"
                {...navProps(REACT_APP_MISP_URL, true)}
              />
            )}
            {modules.ontologies && (
              <NavItem icon={<AccountTreeIcon />} label="Ontologies" {...navProps("/ontologies")} />
            )}
            {modules.taxonomies && (
              <NavItem icon={<CategoryIcon />} label="Taxonomies" {...navProps("/taxonomies")} />
            )}
          </List>

          <SectionLabel open={open}>System</SectionLabel>
          <List sx={{ px: 0.5, py: 0 }}>
            <NavItem icon={<SettingsIcon />} label="Settings" {...navProps("/settings")} />
          </List>
        </Box>

        <Popover
          open={Boolean(gatheringAnchor)}
          anchorEl={gatheringAnchor}
          onClose={closeGatheringPreview}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          disableRestoreFocus
          PaperProps={{
            sx: { ml: 2, p: 7, minWidth: 360, borderRadius: 2 },
            onMouseEnter: () => hoverTimerRef.current && clearTimeout(hoverTimerRef.current),
            onMouseLeave: closeGatheringPreview,
          }}
          sx={{ pointerEvents: "none" }}
          slotProps={{ paper: { sx: { pointerEvents: "auto" } } }}
        >
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2, letterSpacing: "0.08em", fontWeight: 700 }}>
            ACTIVE SOURCES
          </Typography>
          <Stack spacing={1.75}>
            {modules.crawlers && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PublicIcon fontSize="small" sx={{ color: "#4FB6BC" }} />
                <Typography variant="body2" sx={{ minWidth: 80 }}>Web</Typography>
                <Typography variant="body2" fontWeight={700}>{gatheringStats.web}</Typography>
              </Stack>
            )}
            {modules.social_crawlers && (
              <>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <RedditIcon fontSize="small" sx={{ color: "#FF4500" }} />
                  <Typography variant="body2" sx={{ minWidth: 80 }}>Reddit</Typography>
                  <Typography variant="body2" fontWeight={700}>{gatheringStats.reddit}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <ForumIcon fontSize="small" sx={{ color: "#6364FF" }} />
                  <Typography variant="body2" sx={{ minWidth: 80 }}>Mastodon</Typography>
                  <Typography variant="body2" fontWeight={700}>{gatheringStats.mastodon}</Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Popover>
      </Drawer>

      <ThemePalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      <MyContent open={open} noPadding={isFullBleed}>
        {!isFullBleed && <DrawerHeader />}
        <Box sx={isFullBleed ? { flex: 1, overflow: 'hidden' } : undefined}>
          {props.children}
        </Box>
      </MyContent>
    </Box>
  );
};

export default Page;
